import React, {forwardRef, useRef} from 'react'
import {Button, Link, Skeleton as ChakraSkeleton} from '@chakra-ui/react'
import {BasketIcon} from '../icons'
import {FormattedMessage} from 'react-intl'
import PropTypes from 'prop-types'
import {useNotifyMeModal, NotifyModal} from '../../hooks/use-notify-me-modal'
import {
    useRedirectLinkPartnerModal,
    RedirectLinkPartnerModal
} from '../../hooks/use-redirect-link-partner-modal'
import {noop} from '../../utils/utils'
import {useDerivedProduct} from '../../hooks'
import {useAddToCart} from '../../hooks/use-add-to-cart'
import {useCommerceData} from '../../hooks/use-commerce-data'

// Component Skeleton
export const Skeleton = forwardRef((_props, ref) => (
    <ChakraSkeleton ref={ref} data-testid="sf-add-to-cart-skeleton" width="100%" height={14} />
))
if (process.env.NODE_ENV !== 'production') {
    Skeleton.displayName = 'Skeleton'
}

const AddToCartCTA = ({
    label,
    product,
    quantity,
    promotions: _promotions,
    includeInAddToCartModal = true,
    showAddToCartModal = true,
    disableNotifyMe = false,
    isLoading: isProductLoading,
    scrollToTop = true,
    onClick = noop,
    onAfterAddToCart = noop,
    onRecommendedProductClick,
    variant = 'solidGreen',
    ...styles
}) => {
    const notifyButtonRef = useRef(null)
    const addToCartButtonRef = useRef(null)

    const redirectProps = useRedirectLinkPartnerModal({product, onClick})
    const {
        onOpen: onOpenPartnerRedirect,
        isAvailable: isOrderableOnOtherSites,
        partnerBrandButtonColor,
        partnerLink
    } = redirectProps

    const {onOpen: onOpenNotifyMeModal, ...notifyMeModalProps} = useNotifyMeModal()
    const {isOrderable} = useDerivedProduct(product)
    const {handleAddToCart, isLoading} = useAddToCart(product, {
        isLoading: isProductLoading,
        includeInAddToCartModal,
        quantity,
        showAddToCartModal,
        onRecommendedProductClick,
        finalFocusRef: addToCartButtonRef
    })
    const {isEcommerceEnabled} = useCommerceData()

    // content only sites do not render add to cart/notify me buttons
    if (!isEcommerceEnabled && !isOrderableOnOtherSites) {
        return null
    }

    return (
        <>
            <RedirectLinkPartnerModal {...redirectProps} />
            <>
                {isOrderable || isOrderableOnOtherSites ? (
                    <>
                        <Button
                            {...(partnerLink &&
                                !onOpenPartnerRedirect && {
                                    as: Link,
                                    isExternal: true,
                                    href: partnerLink
                                })}
                            onClick={
                                isOrderableOnOtherSites
                                    ? onOpenPartnerRedirect ?? onClick
                                    : (e) => {
                                          scrollToTop &&
                                              window.scrollTo({
                                                  top: 0,
                                                  behavior: 'smooth'
                                              })
                                          onClick?.(e)
                                          handleAddToCart({
                                              onAfterAddToCart,
                                              onRecommendedProductClick,
                                              finalFocusRef: addToCartButtonRef
                                          })
                                      }
                            }
                            key="cart-button"
                            width="100%"
                            variant={variant}
                            rightIcon={<BasketIcon boxSize={4} />}
                            isDisabled={isLoading}
                            isLoading={isLoading}
                            className="add-to-cart"
                            ref={addToCartButtonRef}
                            {...styles}
                            {...(partnerBrandButtonColor
                                ? {
                                      bg: partnerBrandButtonColor,
                                      borderColor: partnerBrandButtonColor,
                                      _hover: {bg: partnerBrandButtonColor, color: 'white'}
                                  }
                                : {})}
                        >
                            {isOrderableOnOtherSites ? (
                                <FormattedMessage
                                    id="partner-link.label"
                                    defaultMessage="Shop with our partner"
                                />
                            ) : (
                                label || (
                                    <FormattedMessage
                                        id="product_view.button.add_to_cart"
                                        defaultMessage="Add to Cart"
                                    />
                                )
                            )}
                        </Button>
                    </>
                ) : (
                    <>
                        {!disableNotifyMe && (
                            <NotifyModal finalFocusRef={notifyButtonRef} {...notifyMeModalProps} />
                        )}

                        <Button
                            key="cart-button"
                            ref={notifyButtonRef}
                            onClick={(e) => {
                                onClick()
                                !disableNotifyMe && onOpenNotifyMeModal(product)
                                e?.preventDefault?.()
                                e?.stopPropagation?.()
                            }}
                            width="100%"
                            variant="solid"
                            data-testid="notify"
                            isDisabled={disableNotifyMe}
                            {...styles}
                        >
                            <FormattedMessage id="notify.modal.submit" defaultMessage="Notify me" />
                        </Button>
                    </>
                )}
            </>
        </>
    )
}

AddToCartCTA.propTypes = {
    product: PropTypes.object,
    promotions: PropTypes.array,
    quantity: PropTypes.number,
    label: PropTypes.string,
    includeInAddToCartModal: PropTypes.bool,
    showAddToCartModal: PropTypes.bool,
    disableNotifyMe: PropTypes.bool,
    scrollToTop: PropTypes.bool,
    isLoading: PropTypes.bool,
    onClick: PropTypes.func,
    onAfterAddToCart: PropTypes.func,
    onRecommendedProductClick: PropTypes.func,
    variant: PropTypes.string
}
export default AddToCartCTA
